/**
 * Prophets Hub — realtime + refetch synchronization layer.
 *
 * Design goals
 * ------------
 * 1) Reuse the existing WebSocket connection (`getSocket` in `./socket.js`) so
 *    we don't open a second socket per Hub tab. All Hub-scoped events are
 *    namespaced with a `hub:` prefix (e.g. `hub:transmission_changed`).
 * 2) Provide a tiny hook API so any Hub page can subscribe to the events it
 *    cares about without pulling in a full query library:
 *
 *      useHubEvent("hub:marketplace_item_updated", () => reload());
 *      useHubPoll(reload, { intervalMs: 15000 });
 *      useVisibilityRefetch(reload);
 *
 * 3) Polling is visibility-gated and does NOT overlap with itself. This is
 *    the fallback path when the WS drops or the client is on a stale pod.
 * 4) All hooks are safe on unmount: subscriptions are cleaned up and pending
 *    timers cleared.
 *
 * NOTE: We intentionally do NOT rebuild the pages on top of react-query.
 * The Hub pages already own their own state + refetch callbacks — this file
 * only wires realtime signals + polling into those existing callbacks.
 */

import { useEffect, useRef, useCallback } from "react";
import { getSocket } from "./socket";

/** Known Hub event types (documented for reference / grep-ability). */
export const HUB_EVENTS = Object.freeze({
  // ------- PUBLIC ---------------------------------------------------------
  // Broadcast to every connected socket. Payloads are minimal: only IDs
  // + action strings that reference resources already reachable via
  // public REST. Never contain user_id, wallet, notes, or amounts.
  TRANSMISSION_CHANGED: "hub:transmission_changed",           // {submission_id, action}
  MARKETPLACE_ITEM_UPDATED: "hub:marketplace_item_updated",   // {item_id, action}
  PROPHECY_FEED_CHANGED: "hub:prophecy_feed_changed",         // {}  (bare feed signal)

  // ------- USER-SCOPED ----------------------------------------------------
  // Server delivers ONLY to sockets whose verified session matches the
  // affected user_id. Payloads are bare invalidation signals — the
  // frontend refetches the authenticated endpoint to hydrate.
  MARKETPLACE_PURCHASE: "hub:marketplace_purchase_created",   // {purchase_id}
  FULFILLMENT_UPDATED: "hub:fulfillment_updated",             // {purchase_id}
  PROPHECY_REVEALED: "hub:prophecy_revealed",                 // {reveal_id}
  FRAGMENTS_UPDATED: "hub:fragments_updated",                 // {}

  // ------- ADMIN-SCOPED ---------------------------------------------------
  // Delivered only to sockets that have upgraded to admin via
  // `auth.admin` with a valid bearer token. Payloads still minimal —
  // admin dashboards re-fetch the private endpoint to hydrate details.
  ADMIN_PURCHASE_CREATED: "hub:admin:marketplace_purchase_created", // {purchase_id, item_id}
  ADMIN_FULFILLMENT_UPDATED: "hub:admin:fulfillment_updated",       // {purchase_id}
});

/**
 * Subscribe to one Hub event type.
 * The handler stays in sync with the latest render via a ref so callers don't
 * have to memoize it — they can pass an inline function safely.
 */
export function useHubEvent(type, handler) {
  const ref = useRef(handler);
  useEffect(() => { ref.current = handler; }, [handler]);
  useEffect(() => {
    if (!type) return undefined;
    const sock = getSocket();
    const unsub = sock.subscribe((msg) => {
      // Broadcasts from server.py use `type: "..."`. Hub events use the
      // `hub:*` prefix so they don't collide with race/settings/etc.
      if (msg && msg.type === type) {
        try { ref.current?.(msg); } catch (e) { /* one bad handler must not kill others */ }
      }
    });
    return unsub;
  }, [type]);
}

/**
 * Visibility-gated polling.
 *   - pauses while the tab is hidden
 *   - immediately refetches when the tab regains focus
 *   - guarantees no overlapping in-flight refetches
 *   - no polling in an interval smaller than 3 seconds (guardrail)
 */
export function useHubPoll(refetchFn, { intervalMs = 15000, enabled = true } = {}) {
  const fnRef = useRef(refetchFn);
  useEffect(() => { fnRef.current = refetchFn; }, [refetchFn]);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!enabled || !fnRef.current) return undefined;
    const safeInterval = Math.max(3000, intervalMs);
    let timer = null;

    const tick = async () => {
      if (document.hidden) return; // paused while hidden
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try { await fnRef.current(); } catch (_) { /* ignore, next tick will retry */ }
      finally { inFlightRef.current = false; }
    };

    const start = () => {
      if (timer) return;
      timer = setInterval(tick, safeInterval);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        // Refresh immediately on return, then resume interval.
        tick();
        start();
      }
    };
    const onFocus = () => { if (!document.hidden) tick(); };

    start();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [intervalMs, enabled]);
}

/**
 * Refetch when the tab regains visibility / window focus.
 * Cheap, event-driven only (no interval).
 */
export function useVisibilityRefetch(refetchFn, enabled = true) {
  const fnRef = useRef(refetchFn);
  useEffect(() => { fnRef.current = refetchFn; }, [refetchFn]);
  useEffect(() => {
    if (!enabled) return undefined;
    const run = () => { if (!document.hidden) { try { fnRef.current?.(); } catch (_) { /* ignore */ } } };
    document.addEventListener("visibilitychange", run);
    window.addEventListener("focus", run);
    return () => {
      document.removeEventListener("visibilitychange", run);
      window.removeEventListener("focus", run);
    };
  }, [enabled]);
}

/**
 * Small debounce helper — collapses a burst of realtime events (e.g. admin
 * batch actions) into a single refetch. Returns a stable callback and a
 * teardown that flushes pending timers.
 */
export function useDebouncedCallback(fn, delayMs = 250) {
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);
  const timerRef = useRef(null);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  return useCallback((...args) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      try { fnRef.current?.(...args); } catch (_) { /* ignore */ }
    }, delayMs);
  }, [delayMs]);
}


/**
 * Admin sockets need to identify themselves via an in-band `auth.admin`
 * message carrying the admin bearer token — browsers can't set custom WS
 * headers. This hook wires that bootstrap: when a token is provided the
 * shared socket is upgraded to admin (and re-upgraded automatically on
 * every reconnect via the socket's bootstrap replay queue). When the
 * token is cleared (logout) we call `resetIdentity()` to drop the
 * per-connection admin flag server-side.
 *
 * Usage (already wired in `<AuthContext>`):
 *
 *   const token = localStorage.getItem("admin_token");
 *   useAdminSocketIdentity(token);
 */
export function useAdminSocketIdentity(token) {
  const prev = useRef(null);
  useEffect(() => {
    const sock = getSocket();
    if (token) {
      sock.authenticateAdmin(token);
      prev.current = token;
    } else if (prev.current) {
      // Token was cleared (logout). Drop server-side identity.
      sock.resetIdentity();
      prev.current = null;
    }
    return undefined;
  }, [token]);
}

/**
 * Force the shared socket to reconnect. Call this on Hub login/logout so
 * the server re-reads the (new) `hub_session` cookie on the fresh
 * connection and the correct user room is joined.
 */
export function pivotHubSocketIdentity() {
  const sock = getSocket();
  try { sock.resetIdentity(); } catch (_) { /* noop */ }
  try { sock.reconnect(); } catch (_) { /* noop */ }
}
